/**
 * 業務のデータの流れを業務詳細のルート（`/businesses/$businessId`）へ繋ぐ受け口。
 *
 * ビュー本体は入力を prop で受け取り、その prop を URL から埋めるのがこの受け口の役目である。
 * 案件は `?pj=` で受ける。案件が新設した業務にマスターの姿を求めず、案件が消す業務に案件を
 * 重ねた姿を求めない（`usePjNodePresence`。案件から見た業務フローの受け口と同じ）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business データの流れのビューを、URL から埋めた業務の番号と案件とともにルートへ繋ぐ。
 * @graph-connects BusinessDataflowPage [calls] データの流れの描画
 * @graph-connects usePjSearchNumber [calls] 検索条件から案件番号を読む
 * @graph-connects usePjNodePresence [calls] 案件が新設した節点か・消す節点かの判定
 */
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { Link, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { NotFoundState } from '../../components/spec/fallback-states.js';
import { BusinessDataflowPage } from '../BusinessDataflowPage.js';
import { pjLinkSearch } from '../pj-link.js';
import { usePjNodePresence } from '../pj-master-presence.js';
import { BUSINESS_DETAIL_ROUTE, BUSINESS_MAP_ROUTE } from '../route-paths.js';
import { pjLinkValue } from '../view-links/common.js';
import { usePjSearchNumber } from './pj-number.js';

/**
 * データの流れのルートの中身。案件が読めればその姿を、読めなければマスターの姿を描く。
 *
 * 業務の番号はパスから文字列で来るので数に直し、数でない値は未存在として業務マップへ戻る導線を
 * 添える。
 *
 * @returns データの流れの画面
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business データの流れのルートへ、検索条件の案件の姿を描く。
 * @graph-connects BusinessDataflowPage [calls] データの流れの描画
 * @graph-connects BusinessDataflowPjBody [calls] 案件を重ねたデータの流れの描画
 * @graph-connects usePjSearchNumber [calls] 検索条件から案件番号を読む
 */
export function BusinessDataflowRoutePage() {
  const { businessId } = useParams({ from: BUSINESS_DETAIL_ROUTE });
  const pjNumber = usePjSearchNumber();
  const { t } = useTranslation();
  const id = Number(businessId);

  if (!Number.isInteger(id) || id <= 0) {
    return (
      <NotFoundState
        id={businessId}
        action={
          <Link
            to={BUSINESS_MAP_ROUTE}
            {...pjLinkSearch(pjLinkValue(pjNumber ?? undefined))}
            className="text-sm text-[var(--color-accent-primary)] underline"
          >
            {t('spec.backToList')}
          </Link>
        }
      />
    );
  }
  if (pjNumber === null) return <BusinessDataflowPage businessId={id} />;
  return <BusinessDataflowPjBody businessId={id} pjNumber={pjNumber} />;
}

/**
 * 案件を重ねたデータの流れ。マスターと案件を重ねた姿を引いてよいかが決まってから描く。
 *
 * @param props 業務の番号・案件番号
 * @returns 読み込み中 / 案件を重ねたデータの流れ
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件が新設した業務か・消す業務かを確かめてから、案件を重ねたデータの流れを描く。
 * @graph-connects BusinessDataflowPage [calls] データの流れの描画
 * @graph-connects usePjNodePresence [calls] マスターと案件を重ねた姿に業務が居るかの判定
 */
function BusinessDataflowPjBody({
  businessId,
  pjNumber,
}: {
  businessId: number;
  pjNumber: number;
}) {
  const { t } = useTranslation();
  const presence = usePjNodePresence(pjNumber, businessId);

  if (presence.master === 'unknown') {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('common.loading')}>
        <Skeleton variant="rectangular" height="6rem" />
        <Skeleton variant="rectangular" height="24rem" />
      </div>
    );
  }

  return (
    <BusinessDataflowPage
      businessId={businessId}
      pjNumber={pjNumber}
      masterAbsent={presence.master === 'absent'}
      appliedAbsent={presence.applied === 'absent'}
    />
  );
}
